import React from 'react';
import { createSelector } from 'reselect';

import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';
import { accountsSelectors } from 'companion-app-components/flux/accounts';

import { doAccountRediscovery } from 'data/AccountDiscovery/actions';
import { refreshAccounts } from 'data/institutionLogins/actions';
import { authSelectors } from 'companion-app-components/flux/auth';
import { getLogger } from 'companion-app-components/utils/core';
import { getIsFeatureAllowed, AllFeatures } from 'data/datasetSharing/selectors';

import { getQcsAggregationAction } from './errors';

const log = getLogger('aggErrors/selectors');

export const getAggregationAction = createSelector(
  (state, props) => props?.institutionLogin,
  accountsSelectors.getAccountsByInstitutionLoginId,
  authSelectors.getIsWebfirstDataset,
  (state) => getIsFeatureAllowed(state, AllFeatures.FIX_ACCOUNT),
  (institutionLogin, accountsByLoginId, isWebFirstDataset, isAllowedToFixAccounts) => {
    // todo: currently this selector only handles a single aggregator, need to update to support multiple aggregators
    if (!institutionLogin || !institutionLogin.aggregators || institutionLogin.aggregators.size === 0) {
      return null;
    }
    if (institutionLogin.aggregators.size > 1) {
      log.warn('Multiple aggregators are not supported');
    }
    const aggregator = institutionLogin.aggregators.get(0);
    if ((aggregator.aggStatus && aggregator.aggStatus !== 'OK') || (institutionLogin.optionalAction)) {
      return getQcsAggregationAction(institutionLogin, aggregator.aggStatus, aggregator.aggStatusCode, featureFlagsSelectors.dangerouslyUseFeatureFlags(), null, isWebFirstDataset, isAllowedToFixAccounts);
    }

    const accountsForLoginId = accountsByLoginId ? accountsByLoginId.get(institutionLogin.id) : null;
    if (!accountsForLoginId || accountsForLoginId.size === 0) {
      // todo: return no accounts error...
      return null;
    }

    // todo: handle DEAD accounts error

    let numDeadAccounts = 0;
    let highestStatusToReport = null;
    let highestStatusCodeToReport = null;
    for (let i = 0; i < accountsForLoginId.size; ++i) {
      const account = accountsForLoginId.get(i);
      if ((!account.aggregators) || (typeof account.aggregators !== 'object')) {
        continue;  // eslint-disable-line no-continue
      }
      // todo: currently this selector only handles a single aggregator, need to update to support multiple aggregators
      if (account.aggregators.size > 1) {
        log.warn('Multiple aggregators are not supported');
      }
      const accountAggregator = account.aggregators.get(0);
      if (accountAggregator.aggStatus && (
        accountAggregator.aggStatus === 'ACCOUNT_MISMATCH' ||
        accountAggregator.aggStatus === 'ACCOUNT_NOT_FOUND' ||
        accountAggregator.aggStatus === 'CP_ACCOUNT_NOT_FOUND')
      ) {
        if (!highestStatusToReport) {
          highestStatusToReport = accountAggregator.aggStatus;
          highestStatusCodeToReport = accountAggregator.aggStatusCode;
        }
        numDeadAccounts += 1;
      }
    }
    if (numDeadAccounts > 0) {
      let accountText = null;
      if (numDeadAccounts === 1) {
        if (accountsForLoginId.size === 1) {
          // We have lost connectivy to your account
          accountText = 'your account';
        } else {
          // We have lost connectivity to one of your accounts
          accountText = 'one of your accounts';
        }
      } else if (numDeadAccounts === accountsForLoginId.size) {
        // We have lost connectivy to all of your accounts
        accountText = 'all of your accounts';
      } else {
        // We have lost connectivity to X of your accounts
        accountText = `${numDeadAccounts} of your accounts`;
      }
      if (featureFlagsSelectors.dangerouslyUseFeatureFlags().get('aggErrorHandleDeadAccounts')) {
        const content = (
          <div>
            We have lost connectivity to {accountText}.
            To restore connectivity, you must reconnect with your FI.
          </div>
        );
        return ({
          // 'ccscrape.324',
          error: 'DEAD_ACCOUNT',
          title: 'This financial institution needs your attention',
          text: content,
          status: highestStatusToReport,
          statusCode: highestStatusCodeToReport,
          code: 'lost.connectivity',
          actionLabel: 'Reconnect',
          action: doAccountRediscovery(institutionLogin),
          showConfirmationDialog: true,
          institutionLogin,
        });
      }
      const content = (
        <div>
          We have lost connectivity to {accountText}.
          To fix you must deactivate and reactivate on your desktop. &nbsp;
          <a
            href="https://www.quicken.com/support/troubleshooting-online-banking-issues-quicken-mobile-and-quicken-web"
            target="_blank"
          >
            Click here for instructions
          </a>
          .
        </div>
      );
      return {
        // 'ccscrape.324',
        error: 'DEAD_ACCOUNT',
        title: 'This financial institution needs your attention',
        text: content,
        status: highestStatusToReport,
        statusCode: highestStatusCodeToReport,
        code: 'lost.connectivity',
        actionLabel: 'Try Again',
        action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
        showConfirmationDialog: true,
        institutionLogin,
      };

    }

    return null;
  },
);
